import * as React from "react";
import { useCallback, useMemo, useRef } from "react";
import { ChatMessage, ComposerConfig, ConversationStore, SuggestionCategory, WelcomeConfig } from "../../types";
import { join } from "../../utils/classNames";
import { PLEXIE_LOGO_URL, PLEXIE_MARK_URL } from "../icons";
import { useChatSessions } from "../../hooks/useChatSessions";
import { ResponseMode, useChat } from "../../hooks/useChat";
import { useSessionEnded } from "../../hooks/useSessionEnded";
import { createParrotHistoryStore } from "../../storage/parrotHistory";
import { ChatSurface } from "../ChatSurface/ChatSurface";
import { SessionSidebar } from "../SessionSidebar/SessionSidebar";
import { SessionRail } from "../SessionSidebar/components/SessionRail";
import "../../styles/tokens.css";
import styles from "./ParrotChat.module.css";

export interface ParrotChatProps {
	/** Agent identifier sent as `agent_type` in the request body. */
	agentType: string;
	/** Optional auth token; sent as `x-auth-token` header when provided. */
	authToken?: string;
	/** The signed-in user's email, scoping conversation history. Takes precedence over the email
	 *  decoded from `authToken` — pass it when the token is opaque (see `ParrotHistoryStoreConfig`).
	 *  Sending messages still requires `authToken`. */
	email?: string;
	/** Extra headers to merge into the request (overrides Content-Type and x-auth-token if set). */
	headers?: Record<string, string>;
	/** How the agent replies. `"json"` (default): one response from `/agents/chat`.
	 *  `"stream"`: server-sent events from `/agents/chat/stream` (AgentCore harness agents),
	 *  rendered as they arrive with a trace of the agent's tool calls. */
	responseMode?: ResponseMode;
	/** Extra fields merged into every agent request body, in both response modes — e.g.
	 *  `{ attachments: [...] }`. Pass a function to compute it when the user sends, so it sees
	 *  the latest state; it runs before `composer.onSend`. Can't override `agent_type`, `query`
	 *  or `session_id`. */
	requestBody?: Record<string, unknown> | (() => Record<string, unknown>);

	/** Controlled sidebar visibility; when undefined, sidebar is hidden. */
	sidebarOpen?: boolean;
	/** When provided, renders a sidebar toggle in the header; called (no args) on click — flip your own `sidebarOpen`. */
	onSidebarToggle?: () => void;

	/** Advanced/testing: override the persistence store. Defaults to the Parrot history API
	 *  store, built internally from agentType + authToken. */
	store?: ConversationStore;

	/** Welcome state (icon, title, subtitle). */
	welcome?: WelcomeConfig;
	/** Suggested prompts grouped into categories. Each category is a pill; clicking it reveals its questions. */
	suggestions?: SuggestionCategory[];
	/** Input placeholder. Default: "Ask…". */
	placeholder?: string;
	/** Optional icon shown next to assistant messages. */
	assistantIconUrl?: string;
	/** Opt-in configuration for the composer's auxiliary controls (attach, model selector, voice,
	 *  attachment chips) and a notification after each send. */
	composer?: ComposerConfig;
	/** Sidebar configuration.
	 *  - `logoUrl`: wordmark shown in the expanded sidebar header.
	 *  - `markUrl`: icon-mark shown in the collapsed rail (hover reveals "open sidebar").
	 *  - `collapseMode`: `"hidden"` (default) fully closes the sidebar, reopened via the header
	 *    toggle; `"rail"` collapses to a compact icon rail instead. */
	sidebar?: { logoUrl?: string; markUrl?: string; collapseMode?: "rail" | "hidden" };

	/** Renders a Files icon button in the chat header when provided. */
	onFiles?: () => void;
	/** Renders a Share button in the chat header when provided. */
	onShare?: () => void;
	/** Animated "rain" glow behind the composer while the agent responds. The effect is a
	 *  dark, glowing halftone that only reads well on dark backgrounds, so it defaults to on
	 *  for the dark theme and off for the light theme. Pass a boolean to force either. */
	respondingAnimation?: boolean;

	/** Minutes of inactivity after the last message before the conversation locks. Default: 30. */
	sessionTimeoutMinutes?: number;
	/** Placeholder shown in the locked input once a session has ended. */
	endedPlaceholder?: string;
	/** Small disclaimer line under the composer. Pass `null` to hide it.
	 *  Default: "PLEXIE can make mistakes and it's bound to your account's data access. Verify Important Data." */
	inputDisclaimer?: string | null;

	/** Renders a close button in the header when provided. */
	onClose?: () => void;
	/** Renders a fullscreen toggle in the header when provided. */
	onFullScreenClick?: () => void;
	/** Drives the header's fullscreen icon (expand vs. collapse). Consumer owns the actual resizing. */
	isFullScreen?: boolean;

	/** Built-in theme. Default "dark". `style`/CSS token overrides still layer on top of either. */
	theme?: "light" | "dark";
	/** When provided, renders a compact light/dark toggle in the header (right of Share).
	 *  `theme` is controlled — the consumer flips its own `theme` state in this handler. */
	onThemeToggle?: () => void;
	/** Optional className applied to the outer wrapper. */
	className?: string;
	/** Optional inline style applied to the outer wrapper (token overrides go here). */
	style?: React.CSSProperties;
}

export const ParrotChat: React.FC<ParrotChatProps> = ({
	agentType,
	authToken,
	email,
	headers,
	responseMode = "json",
	requestBody,
	sidebarOpen,
	onSidebarToggle,
	store,
	welcome,
	suggestions,
	placeholder,
	assistantIconUrl,
	composer,
	sidebar,
	onFiles,
	onShare,
	respondingAnimation,
	sessionTimeoutMinutes = 30,
	endedPlaceholder,
	inputDisclaimer,
	onClose,
	onFullScreenClick,
	isFullScreen,
	theme = "dark",
	onThemeToggle,
	className,
	style,
}) => {
	// Serialize headers so an inline `headers={{…}}` (new object identity each render) doesn't
	// churn the store and re-trigger the load on every render — only its contents matter.
	const headersKey = JSON.stringify(headers ?? {});
	const resolvedStore = useMemo<ConversationStore>(
		() => store ?? createParrotHistoryStore({ agentType, authToken, email, headers }),
		// headersKey is a stable proxy for `headers`
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[store, agentType, authToken, email, headersKey]
	);

	const {
		sessions,
		currentSession,
		currentSessionId,
		createSession,
		switchSession,
		deleteSession,
		renameSession,
		toggleFavourite,
		appendMessages,
		updateMessage,
		recordExchange,
	} = useChatSessions({ agentType, store: resolvedStore });

	const getRemoteSessionId = useCallback(
		() => currentSession?.remoteSessionId,
		[currentSession]
	);

	const handleUserMessage = useCallback(
		(msg: ChatMessage) => {
			if (!currentSessionId) return;
			appendMessages(currentSessionId, [msg]);
		},
		[appendMessages, currentSessionId]
	);

	const handleAssistantMessage = useCallback(
		(msg: ChatMessage, remoteSessionId?: string) => {
			if (!currentSessionId) return;
			appendMessages(currentSessionId, [msg], remoteSessionId);
		},
		[appendMessages, currentSessionId]
	);

	const handleAssistantUpdate = useCallback(
		(messageId: string, patch: Partial<ChatMessage>, remoteSessionId?: string) => {
			if (!currentSessionId) return;
			updateMessage(currentSessionId, messageId, patch, remoteSessionId);
		},
		[updateMessage, currentSessionId]
	);

	const handleExchangeComplete = useCallback(
		(exchange: { userMessage: string; agentMessage: string; remoteSessionId?: string; rawResponse?: unknown }) => {
			if (!currentSessionId) return;
			recordExchange(currentSessionId, exchange);
		},
		[recordExchange, currentSessionId]
	);

	// Latest prop via a ref, so an inline function doesn't churn useChat's callbacks.
	const requestBodyRef = useRef(requestBody);
	requestBodyRef.current = requestBody;
	const getRequestBody = useCallback(() => {
		const rb = requestBodyRef.current;
		return typeof rb === "function" ? rb() : rb;
	}, []);

	const { send, abort, isLoading } = useChat({
		responseMode,
		agentType,
		authToken,
		headers,
		onUserMessage: handleUserMessage,
		onAssistantMessage: handleAssistantMessage,
		onAssistantUpdate: handleAssistantUpdate,
		onExchangeComplete: handleExchangeComplete,
		getRemoteSessionId,
		getRequestBody,
	});

	// Timestamp of the conversation's most recent message (messages are ordered ascending).
	const lastActivityMs = useMemo(() => {
		const last = currentSession?.messages?.at(-1);
		if (!last) return null;
		const t = new Date(last.createdAt).getTime();
		return Number.isNaN(t) ? null : t;
	}, [currentSession]);

	const ended = useSessionEnded(lastActivityMs, sessionTimeoutMinutes * 60_000);

	// Send guard: never post to an ended session, independent of the input's disabled state.
	// Mirrors useChat's own guards too, so `onSend` fires only for a send that goes through.
	const onSend = composer?.onSend;
	const handleSubmit = useCallback(
		(text: string) => {
			if (ended || isLoading || !text.trim()) return;
			send(text);
			onSend?.();
		},
		[ended, isLoading, send, onSend]
	);

	// Default "hidden" preserves pre-sidebar-v2 behavior for consumers that pass
	// `sidebarOpen`/`onSidebarToggle` but not the newer `sidebar` prop: closing fully hides
	// the sidebar and the header toggle reappears. Opt into the compact rail with "rail".
	const collapseMode = sidebar?.collapseMode ?? "hidden";

	return (
		<div
			className={join("parrot-chat-root", theme === "light" && "parrot-chat-light", styles.shell, className)}
			style={style}
		>
			{(() => {
				if (sidebarOpen) {
					return (
						<SessionSidebar
							sessions={sessions}
							currentSessionId={currentSessionId}
							onSwitch={switchSession}
							onCreate={createSession}
							onDelete={deleteSession}
							onRename={renameSession}
							onToggleFavourite={toggleFavourite}
							logoUrl={sidebar?.logoUrl ?? PLEXIE_LOGO_URL}
							onCollapse={onSidebarToggle}
						/>
					);
				}
				if (collapseMode === "rail" && onSidebarToggle) {
					return (
						<SessionRail
							markUrl={sidebar?.markUrl ?? PLEXIE_MARK_URL}
							onExpand={onSidebarToggle}
							onCreate={createSession}
						/>
					);
				}
				return null;
			})()}
			<ChatSurface
				messages={currentSession?.messages || []}
				isLoading={isLoading}
				title={currentSession && currentSession.name !== "New Chat" ? currentSession.name : ""}
				welcome={welcome}
				suggestions={suggestions}
				placeholder={placeholder}
				assistantIconUrl={assistantIconUrl}
				composer={composer}
				favourite={currentSession?.favourite}
				onRenameSession={(name) => currentSessionId && renameSession(currentSessionId, name)}
				onToggleFavourite={() => currentSessionId && toggleFavourite(currentSessionId)}
				onDeleteSession={() => currentSessionId && deleteSession(currentSessionId)}
				onFiles={onFiles}
				onShare={onShare}
				onThemeToggle={onThemeToggle}
				theme={theme}
				respondingAnimation={respondingAnimation ?? theme !== "light"}
				onSubmit={handleSubmit}
				onAbort={abort}
				ended={ended}
				endedPlaceholder={endedPlaceholder}
				inputDisclaimer={inputDisclaimer}
				onSidebarToggle={collapseMode === "hidden" && !sidebarOpen ? onSidebarToggle : undefined}
				sidebarOpen={sidebarOpen}
				onClose={onClose}
				onFullScreenClick={onFullScreenClick}
				isFullScreen={isFullScreen}
			/>
		</div>
	);
};
